import { useState, useEffect, useCallback, useRef } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useNetwork } from '../WalletProvider';
import { getSolanaBalance } from '../services/api/wallet';
import logger from '../utils/logger';

export function useSolBalance() {
  const { publicKey, connected } = useWallet();
  const { network } = useNetwork();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchingRef = useRef(false);
  const mountedRef = useRef(true);

  const fetchBalance = useCallback(async () => {
    // Prevent concurrent fetches
    if (fetchingRef.current) return;
    
    if (!connected || !publicKey) {
      setBalance(null);
      return;
    }

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await getSolanaBalance(publicKey.toString(), network);
      
      // Only update state if component is still mounted
      if (mountedRef.current) {
        if (result && typeof result.balance === 'number') {
          setBalance(result.balance);
        } else {
          throw new Error('Invalid balance response');
        }
      }
    } catch (err) {
      if (mountedRef.current) {
        logger.warn('Unable to fetch balance via backend proxy', { network, error: err.message });
        setError('Unable to fetch balance');
        setBalance(null);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
      fetchingRef.current = false;
    }
  }, [publicKey, connected, network]);

  // Fetch balance when wallet connects or network changes
  useEffect(() => {
    // Add small delay to ensure wallet is fully connected after auto-connect
    const timer = setTimeout(() => {
      if (connected && publicKey) {
        fetchBalance();
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [connected, publicKey, network, fetchBalance]);
  
  // Auto-refresh balance every 30 seconds
  useEffect(() => {
    if (!connected || !publicKey) return;
    
    const interval = setInterval(() => {
      if (connected && publicKey) {
        fetchBalance();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [connected, publicKey, fetchBalance]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    balance,
    loading,
    error,
    refetch: fetchBalance
  };
}

export default useSolBalance;
